import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type { IModuleDefinition } from '@platform/core/types';
import { useMeQuery, useUpdateCustomModuleMutation } from '@platform/state/api';
import { useAppDispatch } from '@platform/state/hooks';
import { toastShown } from '@platform/state/slices/uiSlice';
import type { CustomModulePart } from './CustomModulePart';
import type { ICustomModuleEditor } from './ICustomModuleEditor';
import { normalizePrefix } from './normalizePrefix';
import { saveErrorText } from './saveErrorText';

/**
 * Редактор власного модуля простору (ADR-025; прототип v4 onBName, onBSingular, onBPrefix, onBCat, onBDesc): кожна зміна картки
 * «Визначення» одразу йде PATCH …/custom-modules/{key} (префікс — великими латинськими літерами й цифрами, до 6); порожнє значення не
 * зберігається. Модуль каталогу — undefined (його назву, однину й опис змінює перевизначення ADR-023).
 */
export function useCustomModuleEditor(ws: string, module: IModuleDefinition): ICustomModuleEditor | undefined {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { data: me } = useMeQuery();
  const [update] = useUpdateCustomModuleMutation();
  const key = module.module.key;
  const commit = useCallback((part: CustomModulePart, raw: string) => {
    const value = part === 'prefix' ? normalizePrefix(raw) : raw.trim();
    if (!value) return;
    update({ ws, key, body: { [part]: value } }).unwrap()
      .catch((e: unknown) => dispatch(toastShown({ kind: 'error', text: saveErrorText(e, t('rtBuilder.conflict'), t('rtBuilder.saveFailed')) })));
  }, [update, ws, key, dispatch, t]);
  if (module.module.kind !== 'custom') return undefined;
  return { commit, canEdit: !!me?.workspaces.find((w) => w.id === ws)?.myCapabilities.includes('customize') };
}
